import React from "react";
import { Row, Col, Pagination, Checkbox, Space, Typography, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { useTranslate } from "@refinedev/core";
import { RelationshipCard } from "./RelationshipCard";
import type {
  RelationshipItemData,
  RelationshipGridColumn,
  RelationshipMetadata,
} from "@/types";

const { Text } = Typography;

interface RelationshipCardViewProps {
  items: RelationshipItemData[];
  relationship: RelationshipMetadata;
  booleanColumns: RelationshipGridColumn[];
  nonBooleanColumns: RelationshipGridColumn[];
  loadingCells: Map<string, boolean>;
  bulkLoadingColumns: Map<string, boolean>;
  onBooleanToggle: (
    record: RelationshipItemData,
    columnName: string,
    newValue: boolean,
  ) => void;
  onBulkBooleanToggle: (columnName: string, newValue: boolean) => void;
  onEdit: (record: RelationshipItemData) => void;
  onDelete: (record: RelationshipItemData) => void;
  isDeleteLoading: boolean;
  pagination: { page: number; pageSize: number };
  totalCount: number;
  onPaginationChange: (page: number, pageSize: number) => void;
  isMobile: boolean;
  /** Current items data for computing check-all state */
  allItems: RelationshipItemData[];
}

/**
 * Card-based view for relationship items, optimized for mobile attendance-style UX.
 * Shows large touch-friendly toggles for boolean fields instead of tiny table checkboxes.
 */
export const RelationshipCardView: React.FC<RelationshipCardViewProps> = ({
  items,
  relationship,
  booleanColumns,
  nonBooleanColumns,
  loadingCells,
  bulkLoadingColumns,
  onBooleanToggle,
  onBulkBooleanToggle,
  onEdit,
  onDelete,
  isDeleteLoading,
  pagination,
  totalCount,
  onPaginationChange,
  isMobile,
  allItems,
}) => {
  const translate = useTranslate();

  return (
    <div>
      {/* Bulk action header */}
      {booleanColumns.length > 0 && (
        <div
          style={{
            background: "#fafafa",
            borderRadius: 8,
            padding: "8px 16px",
            marginBottom: 12,
            border: "1px solid #f0f0f0",
          }}
        >
          {booleanColumns.map((col) => {
            const propName = col.name.replace(/_([a-z])/g, (_, l: string) =>
              l.toUpperCase(),
            );
            // Compute check-all state from current page items
            const values = allItems.map((item) => {
              const val = item.relationshipProperties?.[propName];
              return (
                val === true || val === "1" || val === 1 || val === "true"
              );
            });
            const checkedCount = values.filter(Boolean).length;
            const allChecked = checkedCount === values.length && values.length > 0;
            const someChecked = checkedCount > 0 && checkedCount < values.length;
            const isLoading = bulkLoadingColumns.get(col.name);

            return (
              <div
                key={col.key}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "4px 0",
                }}
              >
                <Text style={{ fontSize: 13 }}>{col.label}</Text>
                <Space size={6}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {translate("table.selectAll", "All")}
                  </Text>
                  {isLoading ? (
                    <Spin
                      indicator={
                        <LoadingOutlined style={{ fontSize: 12 }} spin />
                      }
                      size="small"
                    />
                  ) : (
                    <Checkbox
                      checked={allChecked}
                      indeterminate={someChecked && !allChecked}
                      onChange={(e) =>
                        onBulkBooleanToggle(col.name, e.target.checked)
                      }
                      aria-label={`${allChecked ? translate("table.uncheckAll", "Uncheck all") : translate("table.checkAll", "Check all")} ${col.label}`}
                    />
                  )}
                </Space>
              </div>
            );
          })}
        </div>
      )}

      {/* Card list */}
      <Row gutter={[12, 12]}>
        {items.map((record) => (
          <Col key={record.id} xs={24} sm={12}>
            <RelationshipCard
              record={record}
              relationship={relationship}
              booleanColumns={booleanColumns}
              nonBooleanColumns={nonBooleanColumns}
              loadingCells={loadingCells}
              bulkLoadingColumns={bulkLoadingColumns}
              onBooleanToggle={onBooleanToggle}
              onEdit={onEdit}
              onDelete={onDelete}
              isDeleteLoading={isDeleteLoading}
            />
          </Col>
        ))}
      </Row>

      {/* Pagination */}
      <div style={{ marginTop: 16, display: "flex", justifyContent: "end" }}>
        <Pagination
          current={pagination.page}
          pageSize={pagination.pageSize}
          total={totalCount}
          size={isMobile ? "small" : "default"}
          showSizeChanger={!isMobile}
          simple={isMobile}
          showTotal={
            isMobile
              ? undefined
              : (total) =>
                  translate("common.totalItems", { total }, `${total} items`)
          }
          onChange={(page, pageSize) => onPaginationChange(page, pageSize)}
        />
      </div>
    </div>
  );
};

export default RelationshipCardView;
